import React, { useState } from 'react'

import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  DecimalScale,
  FormDialogMode,
  UserPermission
} from '@globaleris/ui.library'
import { useTranslation } from 'react-i18next'

import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'

import { getUserPermissions } from '../../../../helpers/permissionsHelper'
import { useHttpError } from '../../../../hooks/useHttpError'
import { Pages } from '../../../../model/RBAC/constants'

import { LimitDetail } from '../../flexibility.Model'
import { FormDialogDefinitionProps, useFormDialog } from '../../../../hooks'
import moment from 'moment'
import { Add, Delete, Edit, LibraryAdd } from '@material-ui/icons'
import { associateActionIcon } from '../../../../helpers/dataTableActionIconHelper'
import { ActionIcons } from '../../../../model'

export interface QuantityTableProps {
  limitDetails: LimitDetail[] | null | undefined
  canEdit?: boolean
  onAddRowValue: (detail: LimitDetail) => void
  onAddMassiveClickHandler: () => void
  onEditRowValues: (detail: LimitDetail) => void
  onDeleteRow: (id: number | string) => void
  // flexibilityID: number
}

export const QuantityTable: React.FC<QuantityTableProps> = ({
  canEdit = true,
  limitDetails,
  onAddRowValue,
  onAddMassiveClickHandler,
  onEditRowValues,
  onDeleteRow
}) => {
  const [t] = useTranslation(['flexibilityDetails', 'globalerisLibrary', 'enums'])
  const { showError, clearError, errorProps } = useHttpError()
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()
  const [selectedRow, setSelectedRow] = useState<LimitDetail>({} as LimitDetail)

  const detailsIcons = (detailsList: LimitDetail[] | null | undefined): any => {
    if (detailsList) {
      return detailsList.map(p => {
        const actionIcon = p.tempId
          ? associateActionIcon(p, ActionIcons.NEW_ITEM, 'No Guardado')
          : undefined
        return { ...p, actions: actionIcon }
      })
    }
    return detailsList
  }

  const details = detailsIcons(limitDetails)

  // DataTable Config Starts
  const columns: DataTableColumn[] = [
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML,
      columnWidth: 65
    },
    {
      name: 'detailID',
      display: false,
      type: DataType.NUMBER
    },
    {
      name: 'startDate',
      label: t('flexibilityDetails:chips.values.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('flexibilityDetails:chips.values.endDate'),
      type: DataType.DATE
    },
    {
      name: 'quantity',
      label: t('flexibilityDetails:chips.values.quantity'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.CURRENCY
    }
  ]

  const insertHandler = async () => {
    onAddValuesActionHandler()
    return true
  }

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    elevation: 0,
    download: false,
    print: false,
    selectableRows: false,
    rowsPerPage: 20,
    rowsPerPageOptions: [20, 50, 100],
    canInsert: canEdit,
    canEdit: false,
    canDelete: false,
    customInsertEditModal: canEdit,
    onRowInsert: canEdit ? insertHandler : undefined,
    fabCustomActions: [
      {
        userPermission: UserPermission.EDIT,
        icon: <Add fontSize="small" />,
        tooltip: t('flexibilityDetails:fabCustomActions.add.tooltip'),
        onClick: insertHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <LibraryAdd fontSize="small" />,
        tooltip: t('flexibilityDetails:fabCustomActions.addMassive.tooltip'),
        onClick: onAddMassiveClickHandler
      }
    ],
    rowCustomActions: canEdit
      ? [
          {
            userPermission: UserPermission.EDIT,
            icon: <Edit fontSize="small" />,
            tooltip: t('flexibilityDetails:customActions.editValue.tooltip'),
            onClick: (row: LimitDetail) => {
              onEditValuesActionHandler(row)
            }
          },
          {
            userPermission: UserPermission.EDIT,
            icon: <Delete fontSize="small" />,
            tooltip: t('flexibilityDetails:customActions.deletePeriod.tooltip'),
            onClick: (row: LimitDetail) => onDeleteActionHandler(row.idDetail ?? row.tempId)
          }
        ]
      : undefined
  }

  const onAddValuesActionHandler = async () => {
    openForm({
      definition: editValuesDefinition,
      title: t('flexibilityDetails:addValues.title'),
      okBtnLabel: t('flexibilityDetails:addValues.actions.okButton'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: formData => {
        formData ? addRowValue(formData) : closeForm()
      }
    })
    setSelectedRow({} as LimitDetail)
  }

  const onEditValuesActionHandler = async (detail: LimitDetail) => {
    openForm({
      definition: editValuesDefinition,
      title: t('flexibilityDetails:editValues.title'),
      okBtnLabel: t('flexibilityDetails:editValues.actions.okButton'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: detail,
      onClose: formData => {
        formData ? editRowValue(formData) : closeForm()
      }
    })
    setSelectedRow(detail)
  }

  const addRowValue = async (detail: LimitDetail) => {
    onAddRowValue(detail)
    setSelectedRow({} as LimitDetail)
    closeForm()
  }

  const editRowValue = async (coefficient: LimitDetail) => {
    onEditRowValues(coefficient)
    setSelectedRow({} as LimitDetail)
    closeForm()
  }

  const onDeleteActionHandler = async (id: number | string) => {
    onDeleteRow(id)
  }

  const editValuesDefinition: FormDialogDefinitionProps =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_PRICE_VALUES',
        sections: [
          {
            inputs: [
              {
                name: 'startDate',
                label: 'Fecha de inicio',
                type: DataType.DATE,
                required: true,
                onChange(value, formState) {
                  if (moment(value).isValid()) {
                    const date = moment(value).startOf('month').toDate()
                    setSelectedRow(prevState => ({
                      ...prevState,
                      startDate: date
                    }))
                  }
                }
              },
              {
                name: 'endDate',
                label: 'Fecha de fin',
                type: DataType.DATE,
                required: true,
                onChange(value, formState) {
                  if (moment(value).isValid()) {
                    const date = moment(value).endOf('month').toDate()
                    setSelectedRow(prevState => ({
                      ...prevState,
                      endDate: date
                    }))
                  }
                }
              },
              {
                name: 'quantity',
                label: 'Cantidad',
                type: DataType.NUMBER,
                required: true,
                decimalScale: DecimalScale.CURRENCY,
                onChange(value, formState) {
                  setSelectedRow(prevState => ({
                    ...prevState,
                    quantity: +value
                  }))
                }
              }
            ]
          }
        ]
      }
    }, [selectedRow, setSelectedRow])

  // DataTable Config Ends

  return details != null ? (
    <>
      <DataTable
        data={details}
        columns={columns}
        options={options}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.Flexibility)}
      />
      <CustomFormDialog {...formProps} />
    </>
  ) : null
}
